import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isWeekend, isToday } from "date-fns";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

// Safe date converter (handles Firestore Timestamps)





export default function StudentAttendance() {
  const { userData } = useAuth();
  const { getAttendance } = useData();
  const [att, setAtt] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [userData?.uid]);
  const load = async () => {
    setLoading(true);
    const data = await getAttendance({ studentId: userData?.uid });
    setAtt(data); setLoading(false);
  };

  const present = att.filter(a => a.status === "present").length;
  const absent  = att.filter(a => a.status === "absent").length;
  const late    = att.filter(a => a.status === "late").length;
  const total   = att.length;
  const pct     = total > 0 ? Math.round((present / total) * 100) : 0;

  const pieData = [
    { name: "Present", value: present, color: "#059669" },
    { name: "Absent",  value: absent,  color: "#dc2626" },
    { name: "Late",    value: late,    color: "#d97706" },
  ].filter(d => d.value > 0);

  const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
  const getDay = (d) => att.find(a => a.date === format(d, "yyyy-MM-dd"));

  const STATUS = {
    present: { bg: "#ecfdf5", border: "#a7f3d0", text: "#059669", label: "P" },
    absent:  { bg: "#fef2f2", border: "#fca5a5", text: "#dc2626", label: "A" },
    late:    { bg: "#fffbeb", border: "#fde68a", text: "#d97706", label: "L" },
  };

  return (
    <div className="fade-in">
      <div className="page-head">
        <div>
          <h1 className="page-title">✅ My Attendance</h1>
          <p className="page-subtitle">Academic year record · {userData?.className} - {userData?.section}</p>
        </div>
      </div>

      {/* Summary stats */}
      <div className="g g4" style={{ marginBottom: 20 }}>
        {[
          { label: "Total Days", value: total, bg: "var(--blue-50)", color: "var(--blue-700)" },
          { label: "Present",    value: present, bg: "var(--green-50)", color: "var(--green-600)" },
          { label: "Absent",     value: absent,  bg: "var(--red-50)",   color: "var(--red-600)" },
          { label: "Late",       value: late,    bg: "var(--amber-50)", color: "var(--amber-600)" },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: "16px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "1.8rem", fontWeight: 800, color: s.color, fontFamily: "Poppins,sans-serif" }}>{s.value}</div>
            <div style={{ fontSize: ".78rem", color: "var(--gray-500)", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 16, alignItems: "start" }}>
        {/* Calendar */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">📅 Monthly Calendar</h3>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1))}>←</button>
              <span style={{ fontSize: ".85rem", fontWeight: 600, padding: "4px 10px" }}>{format(month, "MMMM yyyy")}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1))}>→</button>
            </div>
          </div>
          <div className="card-body">
            {loading ? <div className="loading-center"><div className="spinner" /></div> : (
              <>
                {/* Day labels */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 6 }}>
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
                    <div key={d} style={{ textAlign: "center", fontSize: ".68rem", fontWeight: 700, color: "var(--gray-400)", padding: "4px 0" }}>{d}</div>
                  ))}
                </div>
                {/* Days grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
                  {Array(days[0].getDay()).fill(null).map((_, i) => <div key={i} />)}
                  {days.map(day => {
                    const rec    = getDay(day);
                    const st     = rec ? STATUS[rec.status] : null;
                    const today  = isToday(day);
                    const wkend  = isWeekend(day);
                    return (
                      <div key={day.toISOString()} style={{
                        borderRadius: "var(--r4)",
                        padding: "6px 4px",
                        textAlign: "center",
                        background: st ? st.bg : wkend ? "var(--gray-50)" : "#fff",
                        border: `1px solid ${st ? st.border : today ? "var(--blue-700)" : "var(--border)"}`,
                        outline: today ? "2px solid var(--blue-100)" : "none",
                      }}>
                        <div style={{ fontSize: ".78rem", fontWeight: today ? 700 : 400, color: st ? st.text : wkend ? "var(--gray-400)" : "var(--gray-700)" }}>
                          {format(day, "d")}
                        </div>
                        {st && <div style={{ fontSize: ".62rem", fontWeight: 800, color: st.text }}>{st.label}</div>}
                        {wkend && !st && <div style={{ fontSize: ".56rem", color: "var(--gray-400)" }}>—</div>}
                      </div>
                    );
                  })}
                </div>
                {/* Legend */}
                <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
                  {Object.entries(STATUS).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: v.bg, border: `1px solid ${v.border}` }} />
                      <span style={{ fontSize: ".72rem", color: "var(--gray-500)" }}>{k.charAt(0).toUpperCase() + k.slice(1)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Pie */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Summary</h3></div>
            <div className="card-body" style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2.4rem", fontWeight: 800, color: pct >= 75 ? "var(--green-600)" : "var(--red-600)", fontFamily: "Poppins,sans-serif" }}>
                {pct}%
              </div>
              <div style={{ fontSize: ".8rem", color: "var(--gray-500)", marginBottom: 12 }}>Overall Attendance</div>
              {total > 0 && (
                <ResponsiveContainer width="100%" height={130}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={36} outerRadius={56}>
                      {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: ".78rem", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {pct < 75 && (
                <div style={{ background: "var(--red-50)", border: "1px solid var(--red-100)", borderRadius: "var(--r6)", padding: "10px", fontSize: ".78rem", color: "var(--red-600)", fontWeight: 600, marginTop: 8 }}>
                  ⚠️ Below 75% minimum. Please attend regularly.
                </div>
              )}
            </div>
          </div>

          {/* Recent */}
          <div className="card">
            <div className="card-header"><h3 className="card-title">Recent Record</h3></div>
            <div className="card-body" style={{ padding: "10px 16px" }}>
              {att.slice(-8).reverse().map(a => {
                const st = STATUS[a.status] || {};
                return (
                  <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #f0f0f0" }}>
                    <span style={{ fontSize: ".82rem" }}>{a.date}</span>
                    <span style={{ fontSize: ".72rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: st.bg, color: st.text }}>
                      {a.status}
                    </span>
                  </div>
                );
              })}
              {att.length === 0 && <div className="empty-state" style={{ padding: "24px 0" }}><div className="empty-icon" style={{ fontSize: "1.8rem" }}>📅</div><div className="empty-title">No records yet</div></div>}
            </div>
          </div>
        </div>
      </div>

      <style>{`@media(max-width:1024px){div[style*="grid-template-columns:1fr 280px"]{grid-template-columns:1fr!important}}`}</style>

    </div>
  );
}